import React, { useEffect, useState } from 'react';
import api from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Loader2, Printer, Search, Download, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { CourseContextFields, coursesAtLocations } from './courseContext';

export function LetterPrintDialog({ template, onClose }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [locations, setLocations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [courseIds, setCourseIds] = useState([]);

  useEffect(() => {
    if (!template) return;
    setSearch(''); setResults([]); setSelected(null);
    const storedCourses = template.course_ids || [];
    setCourseIds(storedCourses);
    api.get('/correspondence/course-options').then((r) => {
      const nextCourses = r.data.courses || [];
      setLocations(r.data.locations || []);
      setCourses(nextCourses);
      setLocationIds([...new Set(
        nextCourses.filter((course) => storedCourses.includes(course.id)).map((course) => course.location_id),
      )]);
    }).catch(() => { setLocationIds([]); });
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); }
    setPdfUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  useEffect(() => {
    if (!template) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get('/guardians', { params: { search: search || undefined, limit: 20 } });
        setResults(r.data);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, template]);

  const generate = async () => {
    if (!selected) { toast.error('Bitte zuerst eine/n Empfänger/in auswählen'); return; }
    setGenerating(true);
    try {
      const r = await api.post('/correspondence/letter-pdf',
        { template_id: template.id, guardian_id: selected.id, course_ids: courseIds },
        { responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })));
    } catch (e) {
      toast.error('Brief konnte nicht erstellt werden');
    } finally { setGenerating(false); }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `Brief_${selected.last_name}_${selected.first_name}.pdf`;
    a.click();
  };

  return (
    <Dialog open={!!template} onOpenChange={(o) => { if (!o) { if (pdfUrl) URL.revokeObjectURL(pdfUrl); onClose(); } }}>
      <DialogContent className={pdfUrl ? 'max-w-4xl h-[88vh] flex flex-col' : 'max-w-lg'} data-testid="letter-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5 text-brand-red" /> Brief drucken</DialogTitle>
          <DialogDescription>
            Vorlage „{template?.name}“ – Briefkopf, Empfängeradresse und Platzhalter werden automatisch eingesetzt.
          </DialogDescription>
        </DialogHeader>

        {pdfUrl ? (
          <>
            <iframe src={pdfUrl} title="Brief" className="flex-1 w-full rounded border border-slate-200" data-testid="letter-print-iframe" />
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }} data-testid="letter-print-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Anderer Empfänger
              </Button>
              <Button onClick={download} data-testid="letter-print-download-btn">
                <Download className="h-4 w-4 mr-1.5" /> PDF herunterladen
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <CourseContextFields
              locations={locations}
              courses={courses}
              locationIds={locationIds}
              courseIds={courseIds}
              onLocations={(ids) => {
                setLocationIds(ids);
                const allowed = new Set(coursesAtLocations(courses, ids).map((course) => course.id));
                setCourseIds((current) => current.filter((id) => allowed.has(id)));
              }}
              onCourses={setCourseIds}
            />
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Empfänger/in suchen (Name oder E-Mail)…" className="pl-9" autoFocus data-testid="letter-print-search" />
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="letter-print-results">
              {searching ? (
                <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
              ) : results.length ? results.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSelected(g)}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${selected?.id === g.id ? 'bg-brand-red/5' : ''}`}
                  data-testid={`letter-print-recipient-${g.id}`}
                >
                  <div className="text-sm font-medium text-slate-900">{g.last_name}, {g.first_name}</div>
                  <div className="text-xs text-slate-500">{g.email || 'keine E-Mail'}</div>
                </button>
              )) : <div className="py-6 text-center text-sm text-slate-400">Keine Zahlungspflichtigen gefunden</div>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} data-testid="letter-print-cancel-btn">Abbrechen</Button>
              <Button onClick={generate} disabled={generating || !selected} data-testid="letter-print-generate-btn">
                {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Printer className="h-4 w-4 mr-1.5" />}
                Brief erstellen{selected ? ` für ${selected.first_name} ${selected.last_name}` : ''}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
